import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Flex, Tabs } from '@mantine/core';
import { useInView } from 'react-intersection-observer';

import {
  useGetPatientQuery,
  useGetPatientTransmissionReportsInfiniteQuery
} from '@/store/slices/api/patientsApiSlice';
import { useUnDismissReportMutation } from '@/store/slices/api/reportsApiSlice';
import withErrorBoundary from '@/utils/withErrorBoundary';
import { useTransmissionReportFeature } from '@/hooks/useTransmissionReportFeature';
import InfoMessage from '@/components/InfoMessage';
import SkeletonList from '@/components/SkeletonList';

import CreateReportTrigger from '@/pages/physician/TransmissionReportPage/CreateReport/ReportCreateTrigger';
import {
  GetPatientTxReportsRequest,
  MiniTxReport,
  ReportColor,
  ReportType
} from '@/types/transmissionReport';
import ConfirmationModal from '@/ui/ConfirmationModal';

import ViewAndDownloadModal from '../../PatientProfilePage/components/PatientTabs/PatientReportsTab/components/ReportsList/components/ViewAndDownloadModal';

import PatientReportCard from './PatientReportCard';
import styles from './PatientsReportList.module.scss';

const PatientReportsList = () => {
  const { uuid: patientId } = useParams() as { uuid: string };
  const [activeTab, setActiveTab] = useState<string>('all');
  const { data: patient } = useGetPatientQuery(patientId);
  const [downloadReport, setDownloadReport] = useState<MiniTxReport | null>(null);
  const [activateReportId, setActivateReportId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activateReportMutation, { isLoading: isActivating }] = useUnDismissReportMutation();
  const navigate = useNavigate();
  const { getSpecificReportRoute } = useTransmissionReportFeature();

  const getFilterParams = (): GetPatientTxReportsRequest => {
    switch (activeTab) {
      case 'routine':
        return {
          colors: [ReportColor.Green, ReportColor.Gray]
        };
      case 'alerts':
        return {
          colors: [ReportColor.Red, ReportColor.Yellow]
        };
      case 'in-clinic':
        return {
          transmissionTypes: [ReportType.InClinic]
        };
      default:
        return {};
    }
  };

  const {
    data,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch
  } = useGetPatientTransmissionReportsInfiniteQuery(
    {
      patientId,
      limit: 10,
      ...getFilterParams()
    },
    {
      refetchOnMountOrArgChange: true
    }
  );

  const allReports = data?.pages.flatMap((page) => page.data) ?? [];

  const { ref: loadMoreRef, inView } = useInView({
    rootMargin: '50px 0px',
    threshold: 0.1,
    triggerOnce: false
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const isCombinedPdf =
    patient?.clinic?.parent?.saveCombinedPDF ?? patient?.clinic?.saveCombinedPDF ?? false;

  const handleDownloadReport = useCallback((report: MiniTxReport) => {
    setDownloadReport(report);
  }, []);

  const handleActivateReportModal = useCallback((id: number | null) => {
    setActivateReportId(id);
    setIsModalOpen(!!id);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setActivateReportId(null);
  }, []);

  const confirmChanges = useCallback(async () => {
    if (activateReportId) {
      try {
        await activateReportMutation(activateReportId).unwrap();
        closeModal();
        navigate(getSpecificReportRoute(activateReportId.toString()));
      } catch (e) {
        closeModal();
      }
    }
  }, [activateReportId, activateReportMutation, closeModal, navigate]);

  if ((isFetching && !isFetchingNextPage) || isLoading) {
    return (
      <Flex direction={'column'} gap={'md'}>
        <SkeletonList items={5} itemHeight="40px" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction={'column'} gap={'md'}>
        <InfoMessage
          title={'Error Loading Reports'}
          message={'Please try refreshing the page or contact support if the issue persists.'}
          c="red"
        />
      </Flex>
    );
  }

  return (
    <Flex direction={'column'}>
      <Flex className={styles.filterHeaderSection} justify={'space-between'} align={'center'}>
        <Tabs
          classNames={{
            tab: styles.filterTabs
          }}
          value={activeTab}
          onChange={(value) => setActiveTab(value || 'all')}
          variant="pills">
          <Tabs.List>
            <Tabs.Tab value="all">All</Tabs.Tab>
            <Tabs.Tab value="routine">Routine</Tabs.Tab>
            <Tabs.Tab value="alerts">Alerts</Tabs.Tab>
            <Tabs.Tab value="in-clinic">In Clinic</Tabs.Tab>
          </Tabs.List>
        </Tabs>
        <CreateReportTrigger patientId={patientId} refetch={refetch} />
      </Flex>
      {patient && downloadReport && (
        <ViewAndDownloadModal
          report={downloadReport}
          isCombinedPdf={isCombinedPdf}
          handleDownloadReport={() => {
            setDownloadReport(null);
          }}
          patient={patient}
        />
      )}

      <Flex direction={'column'} my={8}>
        {allReports.length > 0 ? (
          allReports.map((report) => (
            <PatientReportCard
              key={report.id}
              report={report}
              handleDownloadReport={handleDownloadReport}
              handleActiveReport={handleActivateReportModal}
            />
          ))
        ) : (
          <InfoMessage
            title={'No Reports'}
            message={
              activeTab === 'all'
                ? 'No reports available for this patient.'
                : `No ${activeTab} reports found for this patient.`
            }
          />
        )}
      </Flex>
      {hasNextPage && (
        <div ref={loadMoreRef} style={{ height: '20px', marginTop: '10px' }}>
          {isFetchingNextPage && (
            <Flex justify="center" align="center">
              <SkeletonList items={2} itemHeight="40px" />
            </Flex>
          )}
        </div>
      )}
      <ConfirmationModal
        isOpen={isModalOpen}
        onConfirm={confirmChanges}
        onCancel={closeModal}
        onClose={closeModal}
        title="Confirm Activation"
        description="Activating will restore the report's details in Rhythm Synergy using the dismissed report's information."
        isLoading={isActivating}
        primaryButtonText={'Confirm'}
        secondaryButtonText={'Cancel'}
      />
    </Flex>
  );
};

const PatientReportsListWithBoundary = withErrorBoundary(PatientReportsList, {
  fallback: (
    <InfoMessage
      title={'Failed To Load Reports'}
      message={'Please refresh the page or contact support if the issue persists.'}
    />
  )
});

export default PatientReportsListWithBoundary;
